import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthContext from '../../context/AuthContext';
import ExerciseService from '../../services/exerciseService';
import WorkoutService from '../../services/workoutService';

const WorkoutTracker = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [exercises, setExercises] = useState([]);
  const [selectedExercises, setSelectedExercises] = useState([]);
  const [workoutTitle, setWorkoutTitle] = useState('Workout');
  const [workoutDate, setWorkoutDate] = useState(new Date().toISOString().split('T')[0]);
  const [workoutNotes, setWorkoutNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch exercises using the service
        const exercisesData = await ExerciseService.getAllExercises(token);
        
        if (exercisesData.success) {
          setExercises(exercisesData.exercises || []);
        }
        
        // Fetch workout templates using the service
        const templatesData = await WorkoutService.getWorkoutTemplates(token);
        
        if (templatesData.success) {
          setTemplates(templatesData.templates || []);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load data');
        setLoading(false);
      }
    };
    
    fetchData();
  }, [token]);
  
  // Handle adding an exercise to the workout
  const handleAddExercise = (exerciseId) => {
    const exercise = exercises.find(ex => ex._id === exerciseId);
    
    if (exercise) {
      setSelectedExercises([
        ...selectedExercises,
        {
          exercise: exercise._id,
          exerciseName: exercise.name,
          sets: [{ reps: 10, weight: 0, restTime: 60, completed: false }]
        }
      ]);
    }
  };
  
  // Handle removing an exercise from the workout
  const handleRemoveExercise = (index) => {
    const updatedExercises = [...selectedExercises];
    updatedExercises.splice(index, 1);
    setSelectedExercises(updatedExercises);
  };
  
  // Handle adding a set to an exercise
  const handleAddSet = (exerciseIndex) => {
    const updatedExercises = [...selectedExercises];
    const lastSet = updatedExercises[exerciseIndex].sets[updatedExercises[exerciseIndex].sets.length - 1];
    
    updatedExercises[exerciseIndex].sets.push({
      reps: lastSet.reps,
      weight: lastSet.weight,
      restTime: lastSet.restTime,
      completed: false
    });
    
    setSelectedExercises(updatedExercises);
  };
  
  // Handle removing a set from an exercise
  const handleRemoveSet = (exerciseIndex, setIndex) => {
    const updatedExercises = [...selectedExercises];
    updatedExercises[exerciseIndex].sets.splice(setIndex, 1);
    setSelectedExercises(updatedExercises);
  };
  
  // Handle updating set values
  const handleSetChange = (exerciseIndex, setIndex, field, value) => {
    const updatedExercises = [...selectedExercises];
    updatedExercises[exerciseIndex].sets[setIndex][field] = value;
    setSelectedExercises(updatedExercises);
  };
  
  // Handle loading a template
  const handleLoadTemplate = (e) => {
    const templateId = e.target.value;
    setSelectedTemplate(templateId);
    
    if (!templateId) return;
    
    const template = templates.find(t => t._id === templateId);
    
    if (template) {
      setWorkoutTitle(template.title);
      
      // Map template exercises to selected exercises
      const mappedExercises = template.exercises.map(ex => ({
        exercise: ex.exercise._id,
        exerciseName: ex.exercise.name,
        sets: ex.sets.map(set => ({
          reps: set.reps,
          weight: set.weight,
          restTime: set.restTime,
          completed: false
        }))
      }));
      
      setSelectedExercises(mappedExercises);
    }
  };
  
  // Handle saving the workout
  const handleSaveWorkout = async () => {
    if (selectedExercises.length === 0) {
      toast.error('Please add at least one exercise');
      return;
    }
    
    try {
      setSubmitting(true);
      
      // Format workout data
      const workoutData = {
        title: workoutTitle,
        date: workoutDate,
        exercises: selectedExercises.map(ex => ({
          exercise: ex.exercise,
          sets: ex.sets
        })),
        notes: workoutNotes
      };
      
      // Save workout using the service
      const data = await WorkoutService.createWorkout(workoutData, token);
      
      if (data.success) {
        toast.success('Workout saved successfully');
        navigate('/history');
      } else {
        toast.error(data.message || 'Failed to save workout');
      }
    } catch (error) {
      console.error('Error saving workout:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Handle saving as template
  const handleSaveTemplate = async () => {
    if (selectedExercises.length === 0) {
      toast.error('Please add at least one exercise');
      return;
    }
    
    const templateName = prompt('Enter template name:');
    
    if (!templateName) return;
    
    try {
      setSubmitting(true);
      
      // Format template data
      const templateData = {
        title: workoutTitle,
        templateName,
        exercises: selectedExercises.map(ex => ({
          exercise: ex.exercise,
          sets: ex.sets
        }))
      };
      
      // Save template using the service
      const data = await WorkoutService.createWorkoutTemplate(templateData, token);
      
      if (data.success) {
        toast.success('Template saved successfully');
        
        // Update templates list using the service
        const templatesData = await WorkoutService.getWorkoutTemplates(token);
        
        if (templatesData.success) {
          setTemplates(templatesData.templates || []);
        }
      } else {
        toast.error(data.message || 'Failed to save template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return <div className="loading">Loading workout tracker...</div>;
  }
  
  return (
    <div className="workout-tracker">
      <h1>Track Your Workout</h1>
      
      {/* Workout Details */}
      <div className="workout-details">
        <div className="form-group">
          <label htmlFor="workoutTitle">Workout Title</label>
          <input
            type="text"
            id="workoutTitle"
            value={workoutTitle}
            onChange={(e) => setWorkoutTitle(e.target.value)}
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="workoutDate">Date</label>
          <input
            type="date"
            id="workoutDate"
            value={workoutDate}
            onChange={(e) => setWorkoutDate(e.target.value)}
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="templateSelect">Load Template</label>
          <select
            id="templateSelect"
            value={selectedTemplate}
            onChange={handleLoadTemplate}
          >
            <option value="">-- Select Template --</option>
            {templates.map(template => (
              <option key={template._id} value={template._id}>
                {template.templateName}
              </option>
            ))}
          </select>
        </div>
      </div>
      
      {/* Exercise Selection */}
      <div className="exercise-selection">
        <h2>Add Exercises</h2>
        <div className="form-group">
          <label htmlFor="exerciseSelect">Select Exercise</label>
          <select
            id="exerciseSelect"
            onChange={(e) => handleAddExercise(e.target.value)}
            defaultValue=""
          >
            <option value="" disabled>-- Select Exercise --</option>
            {exercises.map(exercise => (
              <option key={exercise._id} value={exercise._id}>
                {exercise.name} ({exercise.category})
              </option>
            ))}
          </select>
        </div>
      </div>
      
      {/* Selected Exercises */}
      <div className="selected-exercises">
        <h2>Workout Plan</h2>
        {selectedExercises.length === 0 ? (
          <p>No exercises added yet. Select exercises above to add them to your workout.</p>
        ) : (
          selectedExercises.map((exercise, exerciseIndex) => (
            <div key={exerciseIndex} className="exercise-card">
              <div className="exercise-header">
                <h3>{exercise.exerciseName}</h3>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={() => handleRemoveExercise(exerciseIndex)}
                >
                  Remove
                </button>
              </div>
              
              <div className="sets-table">
                <table>
                  <thead>
                    <tr>
                      <th>Set</th>
                      <th>Reps</th>
                      <th>Weight (kg)</th>
                      <th>Rest (sec)</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {exercise.sets.map((set, setIndex) => (
                      <tr key={setIndex}>
                        <td>{setIndex + 1}</td>
                        <td>
                          <input
                            type="number"
                            value={set.reps}
                            onChange={(e) => handleSetChange(exerciseIndex, setIndex, 'reps', parseInt(e.target.value))}
                            min="1"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={set.weight}
                            onChange={(e) => handleSetChange(exerciseIndex, setIndex, 'weight', parseFloat(e.target.value))}
                            min="0"
                            step="0.5"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={set.restTime}
                            onChange={(e) => handleSetChange(exerciseIndex, setIndex, 'restTime', parseInt(e.target.value))}
                            min="0"
                            step="5"
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={() => handleRemoveSet(exerciseIndex, setIndex)}
                            disabled={exercise.sets.length <= 1}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => handleAddSet(exerciseIndex)}
                >
                  Add Set
                </button>
              </div>
            </div>
          ))
        )}
      </div>
      
      {/* Workout Notes */}
      <div className="workout-notes">
        <h2>Workout Notes</h2>
        <textarea
          value={workoutNotes}
          onChange={(e) => setWorkoutNotes(e.target.value)}
          placeholder="Add notes about your workout here..."
          rows="4"
        ></textarea>
      </div>
      
      {/* Action Buttons */}
      <div className="action-buttons">
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSaveWorkout}
          disabled={submitting}
        >
          {submitting ? 'Saving...' : 'Save Workout'}
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleSaveTemplate}
          disabled={submitting}
        >
          Save as Template
        </button>
      </div>
    </div>
  );
};

export default WorkoutTracker;
